import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useAuthenticatedSession } from '../../lib/useAuthenticatedSession.js';
import { useShellAccount } from '../../lib/useShellAccount.js';
import { AppShell } from '../AppShell.js';
import { FilterChip, TEXT_SOFT, TEXT_STRONG } from './FlightCheckParts.js';

interface FlightCheckPageProps {
  // Both come from the service, so they are absent until its first answer.
  title?: string | undefined;
  subtitle?: string | undefined;
  children: ReactNode;
}

export function FlightCheckPage({ title, subtitle, children }: FlightCheckPageProps): ReactNode {
  const { session, isPending, onSignOut } = useAuthenticatedSession();
  const account = useShellAccount(session?.user.email);

  if (isPending || session === null) {
    return (
      <Box
        sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppShell
      active="outcomes"
      account={account}
      onLogout={() => void onSignOut()}
      sx={{ px: { xs: '24px', md: '40px', xl: '64px' }, py: { xs: '32px', md: '48px' } }}
    >
      <Stack sx={{ gap: '32px', maxWidth: 1520, mx: 'auto' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '24px',
            flexWrap: 'wrap',
          }}
        >
          <Stack sx={{ gap: '4px', minWidth: 0 }}>
            {title === undefined ? null : (
              <Typography
                component="h1"
                sx={{ fontSize: 28, fontWeight: 500, color: TEXT_STRONG, lineHeight: 'normal' }}
              >
                {title}
              </Typography>
            )}
            {subtitle === undefined ? null : (
              <Typography sx={{ fontSize: 14, color: TEXT_SOFT, lineHeight: 'normal' }}>
                {subtitle}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" sx={{ gap: '12px' }}>
            <FilterChip label="brand" value="All brands" />
            <FilterChip label="date range" value="Last 90 days" />
          </Stack>
        </Stack>
        {children}
      </Stack>
    </AppShell>
  );
}
